import { PaperClipIcon } from "@heroicons/react/24/outline";
import { twMerge } from "tailwind-merge";
import type { AdminEmailMessageListProps } from "../../models/admin-email.models";
import { ADMIN_EMAIL_ACTION_CLASS } from "../../utils/admin-email.constants";
import { formatAdminEmailDate } from "../../utils/admin-email.utils";

// Show one provider page at a time while keeping previous and next controls honest about available cursors.
// Selecting a message opens its content without marking the shared mailbox message read.
const AdminEmailMessageList = ({
  response,
  loading,
  error,
  pageIndex,
  selectedId,
  onSelect,
  onPrevious,
  onNext,
}: AdminEmailMessageListProps) => (
  <section data-test="admin-email-message-list" aria-label="Received emails" aria-busy={loading} className="min-w-0">
    <div className="flex items-center justify-between gap-2 border-b border-gray-200 px-4 py-3 dark:border-gray-700">
      <h2 className="text-sm font-semibold">Inbox</h2>
      <span className="text-xs opacity-60">Newest first</span>
    </div>
    <div className="max-h-[620px] min-h-[300px] overflow-y-auto">
      {loading && (
        <p role="status" className="p-5 text-sm opacity-70">
          Loading emails…
        </p>
      )}
      {error && (
        <p role="alert" data-test="admin-email-list-error" className="p-5 text-sm text-[#BF0002]">
          {error}
        </p>
      )}
      {!loading && !error && response?.messages.length === 0 && (
        <p data-test="admin-email-list-empty" className="p-5 text-sm opacity-70">
          No emails in this inbox.
        </p>
      )}
      {response?.messages.map((message) => (
        <button
          key={message.id}
          type="button"
          data-test="admin-email-open-message"
          aria-pressed={selectedId === message.id}
          className={twMerge(
            "block w-full border-b border-gray-100 p-4 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-green dark:border-gray-700",
            selectedId === message.id ? "bg-[#E9F4F3] dark:bg-gray-700" : "hover:bg-gray-50 dark:hover:bg-gray-700",
          )}
          onClick={() => onSelect(message)}
        >
          <span className="flex items-start justify-between gap-3">
            <span className={twMerge("min-w-0 truncate text-sm", !message.isRead && "font-bold")}>
              {message.from.name || message.from.address || "Unknown sender"}
            </span>
            {!message.isRead && <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-green" aria-label="Unread" />}
          </span>
          <span className={twMerge("mt-1 block truncate text-sm", !message.isRead && "font-semibold")}>
            {message.subject || "(No subject)"}
          </span>
          <span className="mt-1 line-clamp-2 block text-xs leading-5 opacity-60">{message.bodyPreview}</span>
          <span className="mt-2 flex items-center gap-2 text-[11px] opacity-60">
            <time dateTime={message.receivedDateTime}>{formatAdminEmailDate(message.receivedDateTime)}</time>
            {message.hasAttachments && <PaperClipIcon className="h-3.5 w-3.5" aria-label="Has attachments" />}
          </span>
        </button>
      ))}
    </div>
    <div className="flex items-center justify-between gap-2 border-t border-gray-200 p-3 dark:border-gray-700">
      <button
        type="button"
        data-test="admin-email-previous-page"
        className={ADMIN_EMAIL_ACTION_CLASS}
        disabled={loading || pageIndex === 0}
        onClick={onPrevious}
      >
        Previous
      </button>
      <span data-test="admin-email-page-number" className="text-xs opacity-60">
        Page {pageIndex + 1}
      </span>
      <button
        type="button"
        data-test="admin-email-next-page"
        className={ADMIN_EMAIL_ACTION_CLASS}
        disabled={loading || !response?.pagination.hasNext || !response.pagination.nextCursor}
        onClick={onNext}
      >
        Next
      </button>
    </div>
  </section>
);

export default AdminEmailMessageList;
